"use client";

/**
 * DED-970: Add Policy Dialog
 * Dialog for creating new policies with file upload support
 * Styled to match AddProcessDialog
 */

import React, { useState, useRef, useEffect, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenancy } from "@/contexts/TenancyContext";
import { createDocument } from "@/services/documentsService";
import { toast } from "sonner";
import { Loader2, X, FileText } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faXmark, 
  faFileLines, 
  faUpload,
  faSparkles,
  faCalendarClock,
  faRepeat,
  faShieldCheck,
  faCloudArrowUp,
  faLink
} from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ControlCombobox } from '@/components/ui/ControlCombobox';
import { auth } from '@/lib/firebase';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { DatePicker } from "@/components/ui/date-picker";
import { 
  isConfluenceEnabled, 
  exportConfluencePageAsFile, 
  extractConfluencePageId 
} from '@/services/integrationActionsService';
import { SingleUserCombobox } from "@/components/ui/SingleUserCombobox";
import { getAuthUID } from "@/lib/authState";
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";

interface AddPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (newPolicy?: any) => void;
}

// Format file size with appropriate units
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  const decimals = i === 0 ? 0 : i === 1 ? 1 : 2;
  return `${size.toFixed(decimals)} ${sizes[i]}`;
};

// Policy is the only supported document type

// Recurrence frequency options
type RepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';
const frequencyOptions: { value: RepeatFrequency; label: string }[] = [
  { value: 'never', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

export function AddPolicyDialog({ open, onOpenChange, onSuccess }: AddPolicyDialogProps) {
  const { activeTenancy } = useTenancy();
  const companyID = activeTenancy?.id || (window as any).companyID;
  const currentUserId = getAuthUID() || '';
  
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState(currentUserId);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Policy Source state
  const [policySource, setPolicySource] = useState<'upload' | 'confluence'>('upload');
  const [confluenceURL, setConfluenceURL] = useState('');
  const [isImportingConfluence, setIsImportingConfluence] = useState(false);
  const [confluenceError, setConfluenceError] = useState<string | null>(null);
  const [confluenceIntegrationEnabled, setConfluenceIntegrationEnabled] = useState(false);
  
  // Check if Confluence integration is enabled when dialog opens
  useEffect(() => {
    if (open) {
      isConfluenceEnabled().then(setConfluenceIntegrationEnabled);
    }
  }, [open]);
  
  // Related Controls state
  const [relatedControls, setRelatedControls] = useState<(string | number)[]>([]);
  
  // Scheduling state
  const [repeatFrequency, setRepeatFrequency] = useState<RepeatFrequency>('annually');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [nextReviewDate, setNextReviewDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  const isRecurring = repeatFrequency !== 'never';
  
  // Auto-set start date when switching to recurring
  useEffect(() => {
    if (isRecurring && !startDate) {
      const base = cleanDateAndTimeObject(nextReviewDate ?? new Date()) ?? new Date();
      setStartDate(base);
    }
    if (!isRecurring) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [isRecurring, nextReviewDate, startDate]);
  
  // Schedule summary for display
  const scheduleSummary = useMemo(() => {
    if (!isRecurring || !startDate) return null;
    const intervalLabel = repeatInterval === 1 ? 'Repeats' : `Repeats every ${repeatInterval}`;
    const unit = repeatFrequency === 'daily'
      ? repeatInterval === 1 ? 'day' : 'days'
      : repeatFrequency === 'weekly'
        ? repeatInterval === 1 ? 'week' : 'weeks'
        : repeatFrequency === 'monthly'
          ? repeatInterval === 1 ? 'month' : 'months'
          : repeatFrequency === 'quarterly'
            ? repeatInterval === 1 ? 'quarter' : 'quarters'
            : repeatInterval === 1 ? 'year' : 'years';
    const endText = endDate ? ` until ${toLocalDateString(endDate)}` : '';
    return `${intervalLabel} ${repeatInterval === 1 ? '' : unit} starting ${toLocalDateString(startDate)}${endText}`;
  }, [isRecurring, startDate, endDate, repeatInterval, repeatFrequency]);
  
  // Calculate status based on review date (Published if current/future, Overdue if past)
  const calculatedStatus = useMemo(() => {
    if (!nextReviewDate) return 'Published';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reviewDate = new Date(nextReviewDate);
    reviewDate.setHours(0, 0, 0, 0);
    return reviewDate >= today ? 'Published' : 'Overdue';
  }, [nextReviewDate]);
  
  // Set default owner when dialog opens
  useEffect(() => {
    if (open && !owner && currentUserId) {
      setOwner(currentUserId);
    }
  }, [open, currentUserId]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Validation
  const canSave = name.trim().length > 0 && !saving;
  
  // Reset form
  const resetForm = () => {
    setName("");
    setDescription("");
    setOwner(currentUserId);
    setFile(null);
    setPolicySource('upload');
    setConfluenceURL('');
    setConfluenceError(null);
    setRelatedControls([]);
    setRepeatFrequency('annually');
    setRepeatInterval(1);
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    setNextReviewDate(date);
    setStartDate(null);
    setEndDate(null);
  };
  
  // Handle Confluence pull - uses the new Integration Runtime
  const handlePullConfluence = async () => {
    if (!confluenceURL) {
      setConfluenceError('Please enter a Confluence URL');
      return;
    }
    
    // Extract page ID from URL
    const pageId = extractConfluencePageId(confluenceURL);
    if (!pageId) {
      setConfluenceError('Could not extract page ID from URL. Please use a valid Confluence page URL.');
      return;
    }
    
    setIsImportingConfluence(true);
    setConfluenceError(null);
    
    try {
      // Use the new integration service to export the page
      const result = await exportConfluencePageAsFile(pageId, 'pdf');
      
      if (result.success && result.file) {
        setFile(result.file);
        toast.success('Confluence page imported successfully');
      } else {
        throw new Error(result.error || 'Failed to import Confluence page');
      }
    } catch (error: any) {
      console.error('[AddPolicyDialog] Confluence import error:', error);
      setConfluenceError(error.message || 'Failed to import from Confluence');
      toast.error('Failed to import from Confluence');
    } finally {
      setIsImportingConfluence(false);
    }
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };
  
  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };
  
  // Handle save
  const handleSave = async () => {
    if (!canSave || !companyID) return;
    
    setSaving(true);
    try {
      // Build repeat config if recurring - only add to calendar if recurring
      const repeatConfig = isRecurring && startDate ? {
        frequency: repeatFrequency,
        interval: repeatInterval,
        startDate: startDate,
        endDate: endDate,
      } : undefined;
      
      // Get the actual filename from the File object for storage
      const actualFileName = file?.name || `${name.trim()}.pdf`;
      
      // Create document in document library with policy documentCategory
      const document: any = {
        fileName: actualFileName, // Use actual file name for storage path
        displayName: name.trim(), // Use policy name for display
        documentType: 'Policy',
        documentCategory: 'policy', // This marks it as a policy in the document library
        status: calculatedStatus, // Auto-calculated based on review date
        version: '1.0',
        description: description.trim() || undefined,
        uploadedBy: owner || currentUserId,
        dateUploaded: new Date(),
        nextReviewDate: repeatFrequency === 'never' ? undefined : (isRecurring ? undefined : (nextReviewDate || undefined)), // Don't set if "Does not repeat"
        repeat: repeatConfig, // Add recurrence config if set
        addToCalendar: repeatFrequency !== 'never' && isRecurring, // Only add to compliance calendar if not "Does not repeat" and recurring
        relatedControls: relatedControls.length > 0 ? relatedControls : undefined,
        procRef: relatedControls.length > 0 ? relatedControls.map(String) : undefined,
        confluenceURL: policySource === 'confluence' ? confluenceURL : undefined,
      };
      
      console.log('[AddPolicyDialog] Creating document with data:', document, 'file:', file);
      const newDoc = await createDocument(document, file, companyID);
      
      // Build a policy-like object for immediate display in the table
      const newPolicyItem = {
        id: newDoc.docID,
        docID: newDoc.docID,
        policy_name: name.trim(),
        policy_version: '1.0',
        policy_status: calculatedStatus,
        // Include file information for downloads
        fileName: actualFileName,
        file_name: actualFileName,
        displayName: name.trim(),
        policy_history: {
          created_date: new Date(),
          last_reviewed: null,
          policy_owner: owner || currentUserId,
        },
        policy_metadata: {
          policyType: 'document',
          documentCategory: 'policy',
          file_name: actualFileName,
        },
      };
      
      toast.success("Policy created successfully");
      resetForm();
      onOpenChange(false);
      onSuccess?.(newPolicyItem);
    } catch (error) {
      console.error("[AddPolicyDialog] Failed to create policy:", error);
      toast.error("Failed to create policy");
    } finally {
      setSaving(false);
    }
  };
  
  // Handle close
  const handleClose = () => {
    if (!saving) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-3xl! w-3xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={saving}
            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {/* Header */}
          <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
              <FontAwesomeIcon icon={faShieldCheck} className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Add New Policy</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto">
              Create a new policy document. Policies are stored in your document library and can be linked to controls.
            </p>
          </div>

          {/* Form Content - scrollable */}
          <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
            {/* Policy Name */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                  <FontAwesomeIcon icon={faFileLines} className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label htmlFor="policyName" className="block text-sm font-semibold text-foreground mb-1">
                    Policy Name <span className="text-destructive">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    A clear, descriptive name for this policy
                  </p>
                  <Input
                    id="policyName"
                    placeholder="e.g., Information Security Policy"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
              <Label htmlFor="description" className="block text-sm font-semibold text-foreground mb-1">
                Description
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Describe the purpose and scope of this policy
              </p>
              <Textarea
                id="description"
                placeholder="Describe what this policy covers and its objectives..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-white/5 border border-primary/10 min-h-[100px] focus:border-primary/50"
              />
            </div>

            {/* Policy Source Card */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
              <div className="flex items-start gap-4 mb-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-cyan-500/10 to-cyan-500/30 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCloudArrowUp} className="w-6 h-6 text-cyan-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    Policy Source
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Upload a file or import from Confluence
                  </p>
                </div>
              </div>

              {policySource === 'confluence' ? (
                /* Confluence Import */
                <div className="space-y-3">
                  <Input
                    placeholder="Enter Confluence page URL..."
                    value={confluenceURL}
                    onChange={(e) => setConfluenceURL(e.target.value)}
                    className="bg-white/5 border-primary/10 h-11"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={handlePullConfluence}
                      disabled={isImportingConfluence || !confluenceURL}
                    >
                      {isImportingConfluence ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        'Pull from Confluence'
                      )}
                    </Button>
                  </div>
                  {confluenceError && (
                    <p className="text-xs text-red-500">{confluenceError}</p>
                  )}
                  {file && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mt-3">
                      <FileText className="w-8 h-8 text-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setFile(null)} className="shrink-0">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                /* File Upload */
                <>
                  {file ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <FileText className="w-8 h-8 text-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFile(null)}
                        className="shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-primary/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-white/5 transition-all"
                    >
                      <FontAwesomeIcon icon={faUpload} className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PDF, DOC, DOCX, or other document formats
                      </p>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.md,.xls,.xlsx,.ppt,.pptx"
                  />
                </>
              )}

              {/* Toggle between Confluence and File Upload - only show if Confluence is enabled */}
              {confluenceIntegrationEnabled && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Select
                    value={policySource}
                    onValueChange={(value) => setPolicySource(value as 'upload' | 'confluence')}
                  >
                    <SelectTrigger className="bg-white/5 border-primary/10 h-10 w-[200px]">
                      <SelectValue placeholder="Select source type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upload">File Upload</SelectItem>
                      <SelectItem value="confluence">Confluence Import</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Policy Owner */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
              <Label className="block text-sm font-semibold text-foreground mb-3">
                Policy Owner
              </Label>
              <SingleUserCombobox
                selected={owner}
                setSelected={setOwner}
                placeholder="Select owner..."
                className="bg-white/5 border-primary/10 h-11"
              />
            </div>

            {/* Review Schedule Section */}
            <div className="rounded-xl border border-primary/20 bg-linear-to-br from-primary/5 to-primary/15 backdrop-blur-sm p-6 space-y-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCalendarClock} className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <Label className="block text-sm font-semibold text-foreground mb-1">
                    Review Schedule
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Set when this policy should be reviewed. Recurring policies will appear in your compliance calendar.
                  </p>
                </div>
              </div>

              {/* Schedule Summary Badge - displayed over the date field */}
              {scheduleSummary && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 text-amber-500" />
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                    {scheduleSummary}
                  </p>
                </div>
              )}

              {/* Frequency Selection & Date Pickers */}
              {!isRecurring ? (
                /* Non-recurring: Full width grid layout */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Frequency Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Recurrence
                    </Label>
                    <Select
                      value={repeatFrequency}
                      onValueChange={(value) => {
                        const typed = value as RepeatFrequency;
                        setRepeatFrequency(typed);
                        if (typed === 'never') {
                          setStartDate(null);
                          setEndDate(null);
                        } else {
                          const base = cleanDateAndTimeObject(startDate ?? nextReviewDate ?? new Date()) ?? new Date();
                          setStartDate(base);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full rounded-lg">
                        <SelectValue placeholder="Does not repeat" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Review Date - disabled when "Does not repeat" is selected */}
                  <div className="space-y-2">
                    <Label className={`text-xs font-medium uppercase tracking-wider ${repeatFrequency === 'never' ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>
                      Next Review Date
                    </Label>
                    <DatePicker
                      date={nextReviewDate ?? undefined}
                      setDate={(date) => setNextReviewDate(date ?? null)}
                      readOnly={repeatFrequency === 'never'}
                      className={`bg-white/5 border-primary/10 h-11 rounded-lg w-full ${repeatFrequency === 'never' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>
              ) : (
                /* Recurring: Grid layout */
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Frequency Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Recurrence
                    </Label>
                    <Select
                      value={repeatFrequency}
                      onValueChange={(value) => {
                        const typed = value as RepeatFrequency;
                        setRepeatFrequency(typed);
                        if (typed === 'never') {
                          setStartDate(null);
                          setEndDate(null);
                        } else {
                          const base = cleanDateAndTimeObject(startDate ?? nextReviewDate ?? new Date()) ?? new Date();
                          setStartDate(base);
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full rounded-lg">
                        <SelectValue placeholder="Does not repeat" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recurring: Interval controls */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Every
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        value={repeatInterval}
                        onChange={(e) => {
                          const numeric = Number(e.target.value);
                          setRepeatInterval(Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 1);
                        }}
                        className="bg-white/5 border-primary/10 h-11 w-20 rounded-lg text-center"
                      />
                      <span className="text-sm text-muted-foreground">
                        {repeatFrequency === 'daily'
                          ? 'day(s)'
                          : repeatFrequency === 'weekly'
                            ? 'week(s)'
                            : repeatFrequency === 'monthly'
                              ? 'month(s)'
                              : repeatFrequency === 'quarterly'
                                ? 'quarter(s)'
                                : 'year(s)'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recurring: Start/End Date Row */}
              {isRecurring && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Start Date
                    </Label>
                    <DatePicker
                      date={startDate ?? undefined}
                      setDate={(date) => setStartDate(date ?? null)}
                      className="bg-white/5 border-primary/10 h-11 rounded-lg w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      End Date <span className="text-muted-foreground/60">(optional)</span>
                    </Label>
                    <DatePicker
                      date={endDate ?? undefined}
                      setDate={(date) => setEndDate(date ?? null)}
                      className="bg-white/5 border-primary/10 h-11 rounded-lg w-full"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Related Controls Accordion */}
            <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm overflow-hidden">
              <Accordion type="single" collapsible>
                <AccordionItem value="controls" className="border-0">
                  <AccordionTrigger className="px-5 py-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-linear-to-br from-emerald-500/10 to-emerald-500/30 flex items-center justify-center">
                        <FontAwesomeIcon icon={faLink} className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-semibold text-foreground">Related Controls</span>
                        <span className="ml-2 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          {relatedControls.length > 0 ? `${relatedControls.length} mapped` : 'None'}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <p className="text-xs text-muted-foreground mb-3">
                      Link this policy to security controls for compliance mapping
                    </p>
                    <ScrollArea className="max-h-[200px] overflow-auto">
                      <ControlCombobox
                        selected={relatedControls}
                        setSelected={(controls) => setRelatedControls(controls)}
                      />
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Info Note */}
            <div className="rounded-lg bg-primary/5 border border-primary/10 p-4 flex items-start gap-3">
              <FontAwesomeIcon icon={faSparkles} className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Tip:</span> Policies are stored in your document library and will appear in the compliance calendar for review tracking.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
            <p className="text-xs text-muted-foreground">
              {!name.trim()
                ? "Enter a policy name to continue"
                : "Ready to create policy"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button disabled={!canSave} onClick={handleSave} className="min-w-[100px]">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Policy
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddPolicyDialog;
